import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useTableSearch, useCatalogLoader } from '@/hooks/ui';
import { rolePermissionsService } from '@/api/domains/authorization/rolePermissions.service';
import { mapSystemPage, mapRoleSection } from '@/api/domains/authorization/rolePermissions.mapper';
import { customRoleCatalogService } from '@/api/domains/catalog/customRoles/customRoles.service';
import { permissionLevelCatalogService } from '@/api/domains/catalog/permissionLevels/permissionLevels.service';
import type { SystemPage, RoleSection } from '@/api/domains/authorization/rolePermissions.types';
import type { TableColumn } from '@/components/ui/tables/Table';
import { Selector } from '@/components/ui/inputs/selector';
import { usePermissionsExport } from './usePermissionsExport';


interface SectionPermissionChanges {
  read: number;
  create: number;
  write: number;
  delete: number;
}

interface UsePermissionsResult {
  // Panel izquierdo — páginas
  pages: SystemPage[];
  hasMorePages: boolean;
  isPagesLoading: boolean;
  selectedPageId: number | null;
  sentinelRef: React.RefCallback<HTMLDivElement>;
  scrollContainerRef: React.RefCallback<HTMLDivElement>;
  pagesSearchText: string;
  onPageSelect: (pageId: number) => void;
  onPagesSearch: (text: string) => void;

  // Panel derecho — rol
  roleOptions: { value: string; label: string }[];
  selectedRoleId: number | null;
  onRoleChange: (roleId: number | null) => void;

  // Panel derecho — secciones / tabla
  filteredSections: RoleSection[];
  isSectionsLoading: boolean;
  columns: TableColumn<RoleSection>[];
  sectionsSearchText: string;
  onSectionsSearch: (text: string) => void;

  // Niveles de permiso — opciones de los combos
  permissionLevelOptions: { value: string; label: string }[];

  // Recargar catálogo de roles
  reloadRoleOptions: () => Promise<void>;

  // Cambios pendientes
  localChanges: Map<number, SectionPermissionChanges>;
  isDirty: boolean;
  onPermissionChange: (sectionId: number, field: 'read' | 'create' | 'write' | 'delete', value: number) => void;
  onSave: () => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;

  // Exportar
  onExport: () => Promise<void>;
  isExporting: boolean;
}


export function usePermissions(): UsePermissionsResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();

  // ── Panel izquierdo — páginas ─────────────────────────────────────────────
  const [pages, setPages] = useState<SystemPage[]>([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [, setPagesPage] = useState(1);
  const pagesPageSize = 10;
  const [isPagesLoading, setIsPagesLoading] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const latestPagesRequestRef = useRef(0);
  const accumulatedPagesCountRef = useRef(0);
  const pagesPageRef = useRef(1);
  const fetchPagesRef = useRef<() => Promise<void>>(async () => {});
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const sentinelRef = useCallback((el: HTMLDivElement | null) => { setSentinelEl(el); }, []);
  const scrollContainerRef = useCallback(
    (el: HTMLDivElement | null) => { setContainerEl(el); },
    [],
  );

  const {
    searchText: pagesSearchText,
    debouncedSearchText: debouncedPagesSearch,
    setSearchText: setPagesSearchText,
  } = useTableSearch({
    onDebouncedChange: () => {
      setPages([]);
      accumulatedPagesCountRef.current = 0;
      pagesPageRef.current = 1;
      setPagesPage(1);
      setHasMorePages(true);
    },
  });

  const fetchPages = useCallback(async () => {
    setIsPagesLoading(true);
    const requestId = ++latestPagesRequestRef.current;
    const page = pagesPageRef.current;

    const response = await rolePermissionsService.getSystemPages({
      page,
      pageSize: pagesPageSize,
      searchText: debouncedPagesSearch || null,
    });

    if (requestId !== latestPagesRequestRef.current) {
      setIsPagesLoading(false);
      return;
    }

    if (response.success && response.data) {
      const totalRecords = response.data.pagination?.totalRecords ?? 0;
      const mappedData = (response.data.data ?? []).map(mapSystemPage);

      if (page === 1) {
        accumulatedPagesCountRef.current = mappedData.length;
        setPages(mappedData);
      } else {
        accumulatedPagesCountRef.current += mappedData.length;
        setPages((prev) => [...prev, ...mappedData]);
      }

      setHasMorePages(mappedData.length > 0 && accumulatedPagesCountRef.current < totalRecords);
    } else {
      showApiError(response);
    }

    setIsPagesLoading(false);
  }, [debouncedPagesSearch, showApiError]);

  // Keep ref in sync so IntersectionObserver always calls the latest version
  useEffect(() => { fetchPagesRef.current = fetchPages; }, [fetchPages]);

  useEffect(() => { void fetchPages(); }, [fetchPages]);

  useEffect(() => {
    if (!sentinelEl || !containerEl || !hasMorePages || isPagesLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          pagesPageRef.current += 1;
          setPagesPage((prev) => prev + 1);
          void fetchPagesRef.current();
        }
      },
      { root: containerEl, threshold: 0 },
    );

    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [hasMorePages, isPagesLoading, sentinelEl, containerEl]);

  // ── Rol — catálogo custom-roles ───────────────────────────────────────────
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [roleOptions, setRoleOptions] = useState<{ value: string; label: string }[]>([]);

  const reloadRoleOptions = useCallback(async () => {
    const response = await customRoleCatalogService.getCustomRoles();
    if (response.success && response.data) {
      setRoleOptions(
        response.data.map((r) => ({ value: String(r.id), label: r.description })),
      );
    } else {
      showNotification('error', translate('common:errors.unexpected'), 5000);
    }
  }, [showNotification, translate]);

  useEffect(() => { void reloadRoleOptions(); }, [reloadRoleOptions]);

  // ── Niveles de permiso — catálogo ─────────────────────────────────────────
  const { options: permissionLevelOptions } = useCatalogLoader(
    (signal) => permissionLevelCatalogService.getPermissionLevels(signal),
    (l) => ({ value: String(l.level), label: l.description }),
    [],
    false,
    () => showNotification('error', translate('common:errors.unexpected'), 5000),
  );

  // ── Secciones / tabla ─────────────────────────────────────────────────────
  const [sections, setSections] = useState<RoleSection[]>([]);
  const [isSectionsLoading, setIsSectionsLoading] = useState(false);
  const latestSectionsRequestRef = useRef(0);

  const fetchSections = useCallback(async () => {
    if (!selectedRoleId || !selectedPageId) {
      setSections([]);
      return;
    }

    setIsSectionsLoading(true);
    const requestId = ++latestSectionsRequestRef.current;

    const response = await rolePermissionsService.getRoleSections(selectedRoleId, selectedPageId);

    if (requestId !== latestSectionsRequestRef.current) { setIsSectionsLoading(false); return; }

    if (response.success && response.data) {
      setSections((response.data ?? []).map(mapRoleSection));
    } else {
      showApiError(response);
    }

    setIsSectionsLoading(false);
  }, [selectedRoleId, selectedPageId, showApiError]);

  useEffect(() => { void fetchSections(); }, [fetchSections]);

  // ── Búsqueda client-side de secciones ────────────────────────────────────
  const [sectionsSearchText, setSectionsSearchText] = useState('');

  const onSectionsSearch = useCallback((text: string) => {
    setSectionsSearchText(text);
  }, []);

  const filteredSections = useMemo(
    () =>
      sectionsSearchText
        ? sections.filter((s) =>
          s.name.toLowerCase().includes(sectionsSearchText.toLowerCase()),
        )
        : sections,
    [sections, sectionsSearchText],
  );

  // ── Cambios pendientes ────────────────────────────────────────────────────
  const [localChanges, setLocalChanges] = useState<
    Map<number, SectionPermissionChanges>
  >(new Map());
  const [isSaving, setIsSaving] = useState(false);

  const isDirty = localChanges.size > 0;

  const onPermissionChange = useCallback(
    (sectionId: number, field: 'read' | 'create' | 'write' | 'delete', value: number) => {
      setLocalChanges((prev) => {
        const next = new Map(prev);
        const section = sections.find((s) => s.id === sectionId);
        const existing = next.get(sectionId) ?? {
          read: section?.read ?? 0,
          create: section?.create ?? 0,
          write: section?.write ?? 0,
          delete: section?.delete ?? 0,
        };
        next.set(sectionId, { ...existing, [field]: value });
        return next;
      });
    },
    [sections],
  );

  const onCancel = useCallback(() => {
    setLocalChanges(new Map());
  }, []);

  // ── Columnas de la tabla ──────────────────────────────────────────────────
  const columns = useMemo<TableColumn<RoleSection>[]>(() => {
    const getPermissionValue = (
      section: RoleSection,
      field: 'read' | 'create' | 'write' | 'delete',
    ) => {
      const changes = localChanges.get(section.id);
      return String(changes ? changes[field] : section[field]);
    };

    return [
      {
        key: 'name',
        header: translate('authorization:permissions.columns.section'),
        overflowStrategy: 'wrap',
      },
      {
        key: 'read',
        header: translate('authorization:permissions.columns.read'),
        width: 160,
        render: (section) => (
          <Selector
            label=""
            options={permissionLevelOptions}
            value={getPermissionValue(section, 'read')}
            onChange={(value) => onPermissionChange(section.id, 'read', Number(value))}
            portal
            data-testid={`read-selector-${section.id}`}
          />
        ),
      },
      {
        key: 'create',
        header: translate('authorization:permissions.columns.create'),
        width: 160,
        render: (section) => (
          <Selector
            label=""
            options={permissionLevelOptions}
            value={getPermissionValue(section, 'create')}
            onChange={(value) => onPermissionChange(section.id, 'create', Number(value))}
            portal
            data-testid={`create-selector-${section.id}`}
          />
        ),
      },
      {
        key: 'write',
        header: translate('authorization:permissions.columns.save'),
        width: 160,
        render: (section) => (
          <Selector
            label=""
            options={permissionLevelOptions}
            value={getPermissionValue(section, 'write')}
            onChange={(value) => onPermissionChange(section.id, 'write', Number(value))}
            portal
            data-testid={`save-selector-${section.id}`}
          />
        ),
      },
      {
        key: 'delete',
        header: translate('authorization:permissions.columns.delete'),
        width: 160,
        render: (section) => (
          <Selector
            label=""
            options={permissionLevelOptions}
            value={getPermissionValue(section, 'delete')}
            onChange={(value) => onPermissionChange(section.id, 'delete', Number(value))}
            portal
            data-testid={`delete-selector-${section.id}`}
          />
        ),
      },
    ];
  }, [localChanges, permissionLevelOptions, onPermissionChange, translate]);

  const onSave = useCallback(async () => {
    if (!selectedRoleId || !selectedPageId || !isDirty) return;

    setIsSaving(true);

    // Construir payload: secciones con cambios locales, o valores originales si no fueron tocadas
    const sectionsPayload = sections.map((section) => {
      const changes = localChanges.get(section.id);
      return {
        sectionId: section.id,
        read: changes?.read ?? section.read,
        create: changes?.create ?? section.create,
        write: changes?.write ?? section.write,
        delete: changes?.delete ?? section.delete,
      };
    });

    const response = await rolePermissionsService.savePermissions(selectedRoleId, {
      interfaceId: selectedPageId,
      sections: sectionsPayload,
    });

    if (response.success) {
      showNotification('success', translate('authorization:permissions.messages.saveSuccess'), 4000);
      setLocalChanges(new Map());
      void fetchSections();
    } else {
      showApiError(response);
    }

    setIsSaving(false);
  }, [
    selectedRoleId, selectedPageId, isDirty, sections,
    localChanges, showApiError, showNotification, translate, fetchSections,
  ]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const onPageSelect = useCallback((pageId: number) => {
    setSelectedPageId(pageId);
    setLocalChanges(new Map());
  }, []);

  const onRoleChange = useCallback((roleId: number | null) => {
    setSelectedRoleId(roleId);
    setLocalChanges(new Map());
  }, []);

  const onPagesSearch = useCallback(
    (text: string) => { setPagesSearchText(text); },
    [setPagesSearchText],
  );

  // ── Exportar ──────────────────────────────────────────────────────────────
  const { onExport, isExporting } = usePermissionsExport(selectedRoleId);

  return {
    pages,
    hasMorePages,
    isPagesLoading,
    selectedPageId,
    pagesSearchText,
    sentinelRef,
    scrollContainerRef,
    onPageSelect,
    onPagesSearch,
    roleOptions,
    reloadRoleOptions,
    selectedRoleId,
    onRoleChange,
    filteredSections,
    isSectionsLoading,
    columns,
    sectionsSearchText,
    onSectionsSearch,
    permissionLevelOptions,
    localChanges,
    isDirty,
    onPermissionChange,
    onSave,
    onCancel,
    isSaving,
    onExport,
    isExporting,
  };
}

export default usePermissions;
